import {inject, Injectable} from '@angular/core';
import {DCPErrorMessageDisplayEvent} from '../models/error/dcp-error-message-display-events';
import {DcpUiType} from '../models/dcp-cps-configurator.model';
import {DcpErrorHandlingUtil} from '../utils/dcp-error-handling-util';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventService, OccConfig, TranslationService} from '@spartacus/core';
import {
  Configurator,
  ConfiguratorUISettingsConfig,
  OccConfigurator,
  OccConfiguratorVariantNormalizer,
} from '@spartacus/product-configurator/rulebased';
import {FeatureTogglesService} from '../services';
import {valueProviderTokens} from '../token/tokens';
import Validation = OccConfigurator.Validation;
import CustomUiTypeIndicator = Configurator.CustomUiTypeIndicator;
import UiType = Configurator.UiType;

@UntilDestroy()
@Injectable({providedIn: 'root'})
export class DCPOccConfiguratorVariantNormalizer extends OccConfiguratorVariantNormalizer {
  private unleashToggles = inject(valueProviderTokens.FEATURE_TOGGLES);
  public isNewNamingConvention: boolean = false;

  constructor(
    protected occConfig: OccConfig,
    protected translationService: TranslationService,
    protected eventService: EventService,
    configuratorUISettingsConfig: ConfiguratorUISettingsConfig,
    protected featureToggleService: FeatureTogglesService,
  ) {
    super(occConfig, translationService, configuratorUISettingsConfig);
  }

  override convert(
    source: OccConfigurator.Configuration,
    target?: Configurator.Configuration,
  ): Configurator.Configuration {
    this.featureToggleService
      .isEnabled(this.unleashToggles.cpsAddonsNewNamingConvention)
      .pipe(untilDestroyed(this))
      .subscribe((enabled: boolean) => (this.isNewNamingConvention = enabled));
    const results: Configurator.Configuration = super.convert(source as OccConfigurator.Configuration, target);
    if (source.validation) {
      results.validation = source.validation;
      this.emitErrorEvent(results.validation);
    } else {
      results.validation = [];
    }
    this.convertPriceSummary(source as OccConfigurator.Configuration, results);
    if (source.buttonActions) {
      results.buttonActions = source.buttonActions;
    }
    if (source.productName) {
      results.productName = source.productName;
    }
    if (source.pdpUrl) {
      results.pdpUrl = source.pdpUrl;
    }
    return results;
  }

  private emitErrorEvent(validation: Validation[]) {
    const internalError = DcpErrorHandlingUtil.mapValidationToInternalError(validation);
    this.eventService.dispatch(new DCPErrorMessageDisplayEvent(internalError));
  }

  private emitPriceErrorEvent(priceSummary: OccConfigurator.PriceSummary) {
    const internalError = DcpErrorHandlingUtil.mapPricingToInternalError(priceSummary);
    this.eventService.dispatch(new DCPErrorMessageDisplayEvent(internalError));
  }

  override convertGroup(
    source: OccConfigurator.Group,
    groupList: Configurator.Group[],
    flatGroupList: Configurator.Group[],
  ) {
    super.convertGroup(source, groupList, flatGroupList);

    if (!source.validation) return;

    const groupWithValidation = groupList.pop();
    if (!groupWithValidation) return;

    const lastFlatGroup = flatGroupList[flatGroupList.length - 1];
    if (flatGroupList.length && lastFlatGroup.id === groupWithValidation.id) {
      lastFlatGroup.validation = source.validation;
    }

    groupWithValidation.validation = source.validation;
    this.emitErrorEvent(groupWithValidation.validation);
    groupList.push(groupWithValidation);
  }

  override convertAttributeType(sourceAttribute: OccConfigurator.Attribute): Configurator.UiType {
    if (sourceAttribute?.type === OccConfigurator.UiType.RADIO_BUTTON_ADDITIONAL_INPUT) {
      return Configurator.UiType.RADIOBUTTON;
    }

    return super.convertAttributeType(sourceAttribute);
  }

  override convertAttribute(sourceAttribute: OccConfigurator.Attribute, attributeList: Configurator.Attribute[]) {
    super.convertAttribute(sourceAttribute, attributeList);
    const attributeJustAdded = attributeList[attributeList.length - 1];

    attributeJustAdded.longText = sourceAttribute.longText;
    attributeJustAdded.longTextHTMLFormat = sourceAttribute.longTextHTMLFormat;
    attributeJustAdded.attributeDescriptionText = sourceAttribute.attributeDescriptionText;
    attributeJustAdded.showSelectedHtml = sourceAttribute.showSelectedHtml;
    attributeJustAdded.entryFieldMask = sourceAttribute.entryFieldMask;
    attributeJustAdded.elementPlaceholder = sourceAttribute.elementPlaceholder;
    if (sourceAttribute.validation) {
      attributeJustAdded.validation = sourceAttribute.validation;
      this.emitErrorEvent(attributeJustAdded.validation);
    }

    this.setDcpUiType(attributeJustAdded);

    if (attributeJustAdded.uiType === UiType.NOT_IMPLEMENTED) {
      attributeJustAdded.hidden = true;
      attributeJustAdded.visible = false;
    }
    attributeJustAdded.userInput = attributeJustAdded.userInput || sourceAttribute.formattedValue;
  }

  setDcpUiType(attributeJustAdded: Configurator.Attribute) {
    if (attributeJustAdded.name === 'LIC_QUANTITY_ROOT' || attributeJustAdded.name === 'LIC_QUANTITY') {
      attributeJustAdded.uiTypeVariation = attributeJustAdded.uiType + CustomUiTypeIndicator + DcpUiType.QUANTITY;
    } else if (attributeJustAdded.name.includes('_START_DATE')) {
      attributeJustAdded.uiTypeVariation = attributeJustAdded.uiType + CustomUiTypeIndicator + DcpUiType.DATE;
    } else if (attributeJustAdded.name.includes('_DATE')) {
      attributeJustAdded.hidden = true;
      attributeJustAdded.visible = false;
    } else if (attributeJustAdded.name === 'LIC_SPEND_AMOUNT_NUM') {
      attributeJustAdded.uiTypeVariation = attributeJustAdded.uiType + CustomUiTypeIndicator + DcpUiType.SPEND_AMOUNT;
    } else if (attributeJustAdded.groupId?.includes('_ADDONS')) {
      const isRegularAddon = this.isNewNamingConvention
        ? attributeJustAdded.name.endsWith('_MULTI')
        : attributeJustAdded.uiType === UiType.CHECKBOX;
      const isRadioAddon = this.isNewNamingConvention
        ? attributeJustAdded.name.endsWith('_SINGL')
        : attributeJustAdded.uiType === UiType.RADIOBUTTON;

      if (isRegularAddon) {
        attributeJustAdded.uiTypeVariation = attributeJustAdded.uiType + CustomUiTypeIndicator + DcpUiType.ADDON;
      }
      if (isRadioAddon) {
        attributeJustAdded.uiTypeVariation = attributeJustAdded.uiType + CustomUiTypeIndicator + DcpUiType.ADDON_RADIO;
      }
    } else {
      attributeJustAdded.dcpUiType = attributeJustAdded.uiType;
    }
  }

  override convertValue(occValue: OccConfigurator.Value, values: Configurator.Value[]): void {
    const valueImages: Configurator.Image[] = [];
    if (occValue.images) {
      occValue.images.forEach((occImage) => this.convertImage(occImage, valueImages));
    }
    const value: Configurator.Value = {
      valueCode: occValue.key,
      valueDisplay: occValue.langDepName,
      name: occValue.name,
      selected: occValue.selected,
      selectedHtml: occValue.selectedHtml,
      images: valueImages,
      description: occValue.longText,
    };
    values.push(value);
  }

  convertPriceSummary(source: OccConfigurator.Configuration, target: Configurator.Configuration) {
    target.priceSummary = source.priceSummary;
    if (source.priceSummary?.pricingErrorCode) {
      this.emitPriceErrorEvent(source.priceSummary);
    }
  }
}
